﻿<template>
  <div v-if="hasFields" class="custom-field-values">
    <div
      v-for="field in fields"
      :key="field.id"
      :class="{ 'custom-field-values__row--editing': isEditing(field) }"
      class="custom-field-values__row"
    >
      <span :title="field.name" class="custom-field-values__label">{{ field.name }}</span>

      <div class="custom-field-values__value">
        <!-- CHECKBOX: toggles directly, no edit mode needed -->
        <dd-checkbox
          v-if="field.customFieldType === ECustomFieldTypes.Checkbox"
          :model-value="checkboxValueFor(field)"
          :disabled="!canEdit(field) || isSaving(field)"
          dd-size="medium"
          class="custom-field-values__checkbox"
          @change="(checked) => saveValue(field, { checkboxData: checked })"
        />

        <!-- EDIT MODE -->
        <template v-else-if="isEditing(field)">
          <div :class="{ 'custom-field-values__editor--multiline': isMultilineText(field) }" class="custom-field-values__editor">
            <!-- Free-form text grows with its content; Enter adds a line and Ctrl/Cmd+Enter saves -->
            <el-input
              v-if="isMultilineText(field)"
              ref="editor"
              v-model="draft"
              :placeholder="placeholderFor(field)"
              :disabled="isSaving(field)"
              :autosize="{ minRows: 1, maxRows: 8 }"
              type="textarea"
              maxlength="500"
              resize="none"
              size="small"
              @keyup.enter="commitEdit(field)"
              @keyup.esc="cancelEdit()"
            />
            <el-input
              v-else-if="field.customFieldType === ECustomFieldTypes.Text"
              ref="editor"
              v-model="draft"
              :placeholder="placeholderFor(field)"
              :disabled="isSaving(field)"
              maxlength="500"
              size="small"
              @keyup.enter="commitEdit(field)"
              @keyup.esc="cancelEdit()"
            />
            <el-input
              v-else-if="field.customFieldType === ECustomFieldTypes.Number"
              ref="editor"
              v-model="draft"
              :placeholder="placeholderFor(field)"
              :disabled="isSaving(field)"
              :step="numberStepFor(field)"
              type="number"
              size="small"
              @keyup.enter="commitEdit(field)"
              @keyup.esc="cancelEdit()"
            />
            <div class="custom-field-values__editor-actions">
              <dd-icon-button
                :is-disabled="isSaving(field)"
                icon="check"
                type="rounded"
                size="small"
                color="green"
                title="Save"
                @click="commitEdit(field)"
              />
              <dd-icon-button
                :is-disabled="isSaving(field)"
                icon="close"
                type="rounded"
                size="small"
                color="grey"
                title="Cancel"
                @click="cancelEdit()"
              />
              <dd-icon-button
                v-if="hasValue(field)"
                :is-disabled="isSaving(field)"
                danger
                icon="trash"
                type="rounded"
                size="small"
                color="grey"
                title="Clear value"
                @click="clearValue(field)"
              />
            </div>
          </div>
          <p v-if="validationError" class="custom-field-values__error">{{ validationError }}</p>
        </template>

        <!-- DISPLAY MODE -->
        <template v-else>
          <!-- DROPDOWN: a colored select button, like the priority and status pickers. Picking an option saves immediately. -->
          <select-button
            v-if="field.customFieldType === ECustomFieldTypes.Dropdown"
            :model-value="selectedOptionIdFor(field)"
            :options="selectOptionsFor(field)"
            :is-disabled="!canEdit(field) || isSaving(field)"
            :title="field.name"
            size="small"
            class="custom-field-values__select"
            @update:model-value="(optionID) => handleOptionSelected(field, optionID)"
          />
          <!-- DATE: the due date picker, which already handles the mobile action sheet and clearing -->
          <due-date-picker
            v-else-if="field.customFieldType === ECustomFieldTypes.Date"
            :model-value="dateValueFor(field)"
            :is-disabled="!canEdit(field) || isSaving(field)"
            :sheet-title="field.name"
            :button-title-prefix="field.name"
            clear-button-text="Clear date"
            size="small"
            class="custom-field-values__date"
            @update:model-value="(date) => handleDateSelected(field, date)"
          />
          <span
            v-else-if="hasValue(field)"
            :class="{
              'custom-field-values__display--editable': canEdit(field),
              'custom-field-values__display--multiline': isMultilineText(field),
            }"
            :title="canEdit(field) ? 'Click to edit' : null"
            class="custom-field-values__display"
            @click="beginEdit(field)"
          >
            <a
              v-if="linkFor(field)"
              :href="linkFor(field)"
              class="just-a-link"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
            >{{ displayTextFor(field) }}</a>
            <template v-else>{{ displayTextFor(field) }}</template>
            <i v-if="canEdit(field)" class="ddi ddi-edit" />
          </span>
          <span
            v-else-if="canEdit(field)"
            class="custom-field-values__display custom-field-values__display--editable custom-field-values__display--empty"
            @click="beginEdit(field)"
          >{{ emptyTextFor() }}</span>
          <span v-else class="custom-field-values__display custom-field-values__display--empty">&mdash;</span>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import dayjs from '@/helpers/dayjs'

/* Helpers */
import CustomFieldAssistant, {
  ECustomFieldPermissions,
  ECustomFieldTextTypes,
  ECustomFieldTypes,
} from '@/helpers/custom-field-assistant'

/* Data types */
import {
  ICustomField,
  ICustomFieldOption,
  ICustomFieldValue,
  IGenericJSONMessage,
} from '@/api/types/response.types'
import { IIDNamePairWithColor } from '@/types/data-objects/global'

/* API */
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'
import TasksAPI from '@/api/modules/tasks.api'
import ConversationsAPI from '@/api/modules/convos.api'

/* The value payload accepted by the set endpoint. Only the property matching the field type is sent. */
interface ICustomFieldValuePayload {
  checkboxData?: boolean
  dateData?: string
  textData?: string
  numberData?: number
  optionID?: number
}

/* The subset of the item API used here. Tasks and conversations expose identical signatures. */
interface ICustomFieldValuesAPI {
  get_CustomFieldValues(
    entityId: any,
    itemId: any,
  ): Promise<ICustomFieldValue[]>
  put_CustomFieldValueSet(
    entityId: any,
    itemId: any,
    customFieldId: any,
    data: any,
  ): Promise<ICustomFieldValue>
  delete_CustomFieldValueDelete(
    entityId: any,
    itemId: any,
    customFieldId: any,
  ): Promise<IGenericJSONMessage>
}

/* Sentinel ID for the "None" entry in dropdown select buttons */
const NO_OPTION_ID = 0

export default defineComponent({
  name: 'CustomFieldValues',

  props: {
    /* 'project' or 'mailbox' */
    entityType: {
      type: String,
      required: true,
      validator: (value: string) => ['project', 'mailbox'].includes(value),
    },
    /* ID of the project or mailbox */
    entityId: {
      type: Number,
      required: true,
    },
    /* ID of the task or conversation */
    itemId: {
      type: Number,
      required: true,
    },
    /* Forces every field read-only regardless of the field's permissions */
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['value-updated'],

  data() {
    return {
      ECustomFieldTypes,
      fields: [] as ICustomField[],
      values: [] as ICustomFieldValue[],
      editingFieldID: null as number,
      savingFieldID: null as number,
      draft: null as any,
      validationError: '',
    }
  },

  computed: {
    isMailbox(): boolean {
      return this.entityType === 'mailbox'
    },
    valuesApi(): ICustomFieldValuesAPI {
      return this.isMailbox ? ConversationsAPI : TasksAPI
    },
    hasFields(): boolean {
      return this.fields.length > 0
    },
  },

  watch: {
    itemId() {
      this.cancelEdit()
      this.fetchAll()
    },
  },

  async created() {
    await this.fetchAll()
  },

  methods: {
    /* ---------- Data loading ---------- */

    async fetchAll() {
      try {
        const [fields, values] = await Promise.all([
          this.isMailbox
            ? MailboxesAPI.get_CustomFields(this.entityId)
            : InternalProjectsAPI.get_CustomFields(this.entityId),
          this.valuesApi.get_CustomFieldValues(this.entityId, this.itemId),
        ])
        this.fields = fields
        this.values = values
      } catch (error) {
        this.$error.emit(error as any)
      }
    },

    /* Exposed so a parent can refetch after a stale-data refresh */
    async refresh() {
      await this.fetchAll()
    },

    /* ---------- Lookups ---------- */

    valueFor(field: ICustomField): ICustomFieldValue | null {
      return this.values.find(v => v.customFieldID === field.id) || null
    },

    hasValue(field: ICustomField): boolean {
      const value = this.valueFor(field)
      if (!value) {
        return false
      }
      switch (field.customFieldType) {
        case ECustomFieldTypes.Text:
          return !!value.textData
        case ECustomFieldTypes.Number:
          return value.numberData !== null && value.numberData !== undefined
        case ECustomFieldTypes.Dropdown:
          return !!value.optionID
        case ECustomFieldTypes.Date:
          return !!value.dateData
        case ECustomFieldTypes.Checkbox:
          return value.checkboxData !== null && value.checkboxData !== undefined
        default:
          return false
      }
    },

    checkboxValueFor(field: ICustomField): boolean {
      const value = this.valueFor(field)
      return !!(value && value.checkboxData)
    },

    optionFor(field: ICustomField): ICustomFieldOption | null {
      const value = this.valueFor(field)
      if (!value || !value.optionID) {
        return null
      }
      return (field.options || []).find(o => o.id === value.optionID) || null
    },

    /*
     * The select button shows its first option when nothing is selected, so a grey
     * placeholder leads the list. Picking it clears the value.
     */
    selectOptionsFor(field: ICustomField): IIDNamePairWithColor[] {
      const placeholder: IIDNamePairWithColor = {
        id: NO_OPTION_ID,
        name: 'None',
        color: 'grey',
      }
      const options = (field.options || []).map(o => ({
        id: o.id,
        name: o.name,
        color: o.color,
      }))
      return [placeholder, ...options]
    },

    selectedOptionIdFor(field: ICustomField): number {
      const option = this.optionFor(field)
      return option ? option.id : NO_OPTION_ID
    },

    /* The picker works in timezone-agnostic YYYY-MM-DD strings */
    dateValueFor(field: ICustomField): string | null {
      const value = this.valueFor(field)
      return value && value.dateData
        ? dayjs(value.dateData).format('YYYY-MM-DD')
        : null
    },

    async handleDateSelected(field: ICustomField, date: string | null) {
      if (date === this.dateValueFor(field)) {
        return
      }
      if (!date) {
        if (this.hasValue(field)) {
          await this.clearValue(field)
        }
        return
      }
      await this.saveValue(field, { dateData: date })
    },

    async handleOptionSelected(field: ICustomField, optionID: number) {
      const currentID = this.selectedOptionIdFor(field)
      if (optionID === currentID) {
        return
      }
      if (optionID === NO_OPTION_ID) {
        if (this.hasValue(field)) {
          await this.clearValue(field)
        }
        return
      }
      await this.saveValue(field, { optionID })
    },

    canEdit(field: ICustomField): boolean {
      if (this.isDisabled) {
        return false
      }
      if (this.$permissions.isAdminOrOwner) {
        return true
      }
      const permission = this.$permissions.isGuest
        ? field.guestUserPermissions
        : field.normalUserPermissions
      return permission === ECustomFieldPermissions.ViewAndEdit
    },

    isEditing(field: ICustomField): boolean {
      return this.editingFieldID === field.id
    },

    isMultilineText(field: ICustomField): boolean {
      return CustomFieldAssistant.isMultilineText(field)
    },

    isSaving(field: ICustomField): boolean {
      return this.savingFieldID === field.id
    },

    /* ---------- Display formatting ---------- */

    displayTextFor(field: ICustomField): string {
      const value = this.valueFor(field)
      if (!value) {
        return ''
      }
      switch (field.customFieldType) {
        case ECustomFieldTypes.Text:
          return value.textData
        case ECustomFieldTypes.Number:
          return this.formatNumber(field, value.numberData)
        default:
          return ''
      }
    },

    /* Shared with listings and kanban cards so numbers format identically everywhere */
    formatNumber(field: ICustomField, number: number): string {
      return CustomFieldAssistant.formatNumber(field, number)
    },

    linkFor(field: ICustomField): string | null {
      if (field.customFieldType !== ECustomFieldTypes.Text) {
        return null
      }
      const value = this.valueFor(field)
      if (!value || !value.textData) {
        return null
      }
      switch (field.textType) {
        case ECustomFieldTextTypes.Url:
          return value.textData
        case ECustomFieldTextTypes.Email:
          return `mailto:${value.textData}`
        case ECustomFieldTextTypes.PhoneNumber:
          return `tel:${value.textData.replace(/[^\d+]/g, '')}`
        default:
          return null
      }
    },

    emptyTextFor(): string {
      return 'Add a value'
    },

    placeholderFor(field: ICustomField): string {
      return CustomFieldAssistant.placeholderFor(field)
    },

    numberStepFor(field: ICustomField): string {
      return CustomFieldAssistant.numberStepFor(field)
    },

    /* ---------- Editing ---------- */

    beginEdit(field: ICustomField) {
      // Dropdowns, dates and checkboxes edit in place and never enter draft mode
      if (
        !this.canEdit(field) ||
        this.savingFieldID !== null ||
        field.customFieldType === ECustomFieldTypes.Dropdown ||
        field.customFieldType === ECustomFieldTypes.Date ||
        field.customFieldType === ECustomFieldTypes.Checkbox
      ) {
        return
      }

      const value = this.valueFor(field)
      this.validationError = ''
      this.editingFieldID = field.id

      switch (field.customFieldType) {
        case ECustomFieldTypes.Text:
          this.draft = value ? value.textData : ''
          break
        case ECustomFieldTypes.Number:
          this.draft =
            value && value.numberData !== null && value.numberData !== undefined
              ? String(value.numberData)
              : ''
          break
        default:
          this.draft = null
      }

      this.$nextTick(() => {
        const editor = this.$refs.editor as any
        const instance = Array.isArray(editor) ? editor[0] : editor
        if (instance && typeof instance.focus === 'function') {
          instance.focus()
        }
      })
    },

    cancelEdit() {
      this.editingFieldID = null
      this.draft = null
      this.validationError = ''
    },

    /* Validates the draft against the field settings. Returns an error message, or null when valid. */
    validateDraft(field: ICustomField): string | null {
      switch (field.customFieldType) {
        case ECustomFieldTypes.Text:
          return CustomFieldAssistant.validateText(
            field,
            String(this.draft || ''),
          )
        case ECustomFieldTypes.Number:
          return CustomFieldAssistant.validateNumber(
            field,
            String(this.draft === null ? '' : this.draft),
          )
        default:
          return null
      }
    },

    buildPayload(field: ICustomField): ICustomFieldValuePayload {
      switch (field.customFieldType) {
        case ECustomFieldTypes.Text:
          return { textData: String(this.draft || '').trim() }
        case ECustomFieldTypes.Number:
          return { numberData: Number(String(this.draft).trim()) }
        default:
          return {}
      }
    },

    async commitEdit(field: ICustomField) {
      if (!this.isEditing(field) || this.isSaving(field)) {
        return
      }

      // Blanking out a text or number field that already has a value is treated as clearing it
      const isBlank =
        (field.customFieldType === ECustomFieldTypes.Text ||
          field.customFieldType === ECustomFieldTypes.Number) &&
        String(this.draft === null ? '' : this.draft).trim().length === 0

      if (isBlank) {
        if (this.hasValue(field)) {
          await this.clearValue(field)
        } else {
          this.cancelEdit()
        }
        return
      }

      const error = this.validateDraft(field)
      if (error) {
        this.validationError = error
        return
      }

      await this.saveValue(field, this.buildPayload(field))
    },

    async saveValue(field: ICustomField, payload: ICustomFieldValuePayload) {
      if (!this.canEdit(field) || this.isSaving(field)) {
        return
      }

      this.savingFieldID = field.id
      try {
        const saved = await this.valuesApi.put_CustomFieldValueSet(
          this.entityId,
          this.itemId,
          field.id,
          payload,
        )
        const index = this.values.findIndex(v => v.customFieldID === field.id)
        if (index >= 0) {
          this.values.splice(index, 1, saved)
        } else {
          this.values.push(saved)
        }
        this.$emit('value-updated', saved)
        this.cancelEdit()
      } catch (error) {
        this.$error.emit(error as any, true)
      } finally {
        this.savingFieldID = null
      }
    },

    async clearValue(field: ICustomField) {
      if (!this.canEdit(field) || this.isSaving(field)) {
        return
      }

      this.savingFieldID = field.id
      try {
        await this.valuesApi.delete_CustomFieldValueDelete(
          this.entityId,
          this.itemId,
          field.id,
        )
        this.values = this.values.filter(v => v.customFieldID !== field.id)
        this.$emit('value-updated', null)
        this.cancelEdit()
      } catch (error) {
        this.$error.emit(error as any, true)
      } finally {
        this.savingFieldID = null
      }
    },
  },
})
</script>

<style lang="scss">
.custom-field-values {
  display: flex;
  flex-direction: column;
  margin: 4px 0 12px;
  border: $border--solid-light;
  border-radius: 8px;
  background-color: var(--color-background-primary);

  &__row {
    display: flex;
    align-items: flex-start;
    padding: 8px 12px;
    min-height: 40px;

    &:not(:last-child) {
      border-bottom: $border--solid-extra-light;
    }

    &--editing {
      background-color: var(--color-background-secondary);
    }
  }

  &__label {
    flex: 0 0 150px;
    max-width: 150px;
    padding-top: 3px;
    padding-right: 12px;
    @include lettering--p-sm;
    @include single-line-ellipsis;
    color: var(--color-text-third);
    font-weight: $bold;

    @include media-breakpoint-down(sm) {
      flex-basis: 110px;
      max-width: 110px;
    }
  }

  &__value {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__checkbox {
    margin-top: 3px;
  }

  /* Pickers sit flush with the left edge of the value column, matching where text values start */
  &__select {
    align-self: flex-start;
    margin-left: 0;
    max-width: 100%;

    .select-button__button {
      max-width: 100%;
    }
  }

  &__date {
    align-self: flex-start;
    margin-left: 0;
  }

  &__display {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 2px 6px;
    margin-left: -6px;
    border-radius: 4px;
    @include lettering--p-sm;
    color: var(--color-text-primary);
    word-break: break-word;

    .ddi-edit {
      margin-left: 8px;
      font-size: 12px;
      color: var(--color-text-third);
      opacity: 0;
      transition: opacity 0.15s ease-in-out;
    }

    &--editable {
      cursor: pointer;

      &:hover {
        background-color: var(--color-background-secondary);

        .ddi-edit {
          opacity: 1;
        }
      }
    }

    &--empty {
      color: var(--color-text-third);
      font-style: italic;
    }

    /* Free-form text keeps the line breaks the user typed */
    &--multiline {
      white-space: pre-wrap;
      align-items: flex-start;

      .ddi-edit {
        margin-top: 3px;
      }
    }
  }

  &__editor {
    display: flex;
    align-items: center;
    @include flex-row-horizontal-spacing(8px);

    > .el-input,
    > .el-textarea {
      flex: 1;
      min-width: 0;
      max-width: 320px;
    }

    /* Keep the action buttons pinned to the top as the textarea grows */
    &--multiline {
      align-items: flex-start;

      > .el-textarea {
        max-width: 480px;
      }

      .custom-field-values__editor-actions {
        padding-top: 2px;
      }
    }
  }

  &__hint {
    margin: 4px 0 0;
    font-size: 11px;
    line-height: 1.4;
    color: var(--color-text-third);
  }

  &__editor-actions {
    display: flex;
    flex-shrink: 0;
    @include flex-row-horizontal-spacing(4px);
  }

  &__error {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: $strawberry;
  }
}
</style>
